"use client";

import React from "react";
import { SupportChatWidget, toChannelId } from "@wtc/support-chat";
import type {
  SupportChatConfig,
  SupportChatUser,
  SupportChatOfflineFallbackOptions,
  SupportChatRatingOptions,
} from "@wtc/support-chat";
import { useSupportChatOpenOptional } from "@/lib/contexts/support-chat-open-context";
import { isSupportChatChannelConfigured } from "@/lib/feature-flags";
import { useAuth } from "@wtc/ui";

/**
 * Support Chat Button Component
 *
 * Displays a support chat widget. When used within a SupportChatOpenProvider,
 * the widget is controlled from the provider (no floating FAB). Otherwise,
 * displays a floating action button in the bottom right corner.
 * Uses cookie storage to persist chat threads.
 *
 * Passes bearer token for API authentication if available.
 */
export function SupportChatButton() {
  const supportChatContext = useSupportChatOpenOptional();
  const { getAuthToken } = useAuth();
  const [bearerToken, setBearerToken] = React.useState<string | undefined>();

  React.useEffect(() => {
    const token = getAuthToken();
    setBearerToken(token || undefined);
  }, [getAuthToken]);

  const authHeaders = React.useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (bearerToken) headers["Authorization"] = `Bearer ${bearerToken}`;
    return headers;
  }, [bearerToken]);

  // Hub-only: offline fallback → convert a stalled chat into a ticket + email.
  // Passing this prop is what enables the button in the shared widget; no other
  // app wires it, so the feature stays Hub-scoped.
  const offlineFallback = React.useMemo<SupportChatOfflineFallbackOptions>(
    () => ({
      replyTimeoutMs: 120000, // 2 minutes with no agent reply
      onConvertToTicket: async (ctx) => {
        try {
          const res = await fetch("/api/support/convert-to-ticket", {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              threadId: ctx.threadId,
              userEmail: ctx.userEmail,
              userName: ctx.userName,
              transcript: ctx.transcript,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.ok) {
            return {
              ok: false,
              message:
                typeof data?.error === "string" ? data.error : undefined,
            };
          }
          return {
            ok: true,
            message:
              typeof data?.message === "string" ? data.message : undefined,
          };
        } catch {
          return { ok: false };
        }
      },
    }),
    [authHeaders],
  );

  // Hub-only: post-chat 👍/👎 satisfaction rating → customer_feedback.
  const rating = React.useMemo<SupportChatRatingOptions>(
    () => ({
      onRate: async (value, ctx) => {
        try {
          await fetch("/api/support/feedback", {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              threadId: ctx.threadId,
              userEmail: ctx.userEmail,
              rating: value,
            }),
          });
        } catch {
          // Best-effort — a failed rating write isn't worth surfacing.
        }
      },
    }),
    [authHeaders],
  );

  const channelId = process.env.NEXT_PUBLIC_SUPPORT_CHAT_CHANNEL_ID;

  // Shared with the "Live chat" menu item gate (see lib/feature-flags.ts).
  // If this returns null, any affordance that opens the widget must be hidden
  // too — otherwise it becomes a dead-end button.
  if (!isSupportChatChannelConfigured() || !channelId) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Support Chat: NEXT_PUBLIC_SUPPORT_CHAT_CHANNEL_ID is not set");
    }
    return null;
  }

  const config: SupportChatConfig = {
    channelId: toChannelId(channelId),
    apiBaseUrl: "/api/support",
    pollInterval: 2500,
    cookieStorage: {
      cookieName: "hub_support_threads",
      maxAge: 365,
      path: "/",
      secure: typeof window !== "undefined" && window.location.protocol === "https:",
      sameSite: "lax",
    },
  };

  // Start with minimal user info - will be collected via UserInfoForm when chat opens
  const user: SupportChatUser = {
    email: "visitor@hub.local",
    isAuthenticated: false,
  };

  // When context is present, use controlled mode (no FAB wrapper)
  if (supportChatContext) {
    return (
      <SupportChatWidget
        config={config}
        user={user}
        title="Support Chat"
        inputPlaceholder="Type your message..."
        open={supportChatContext.open}
        onOpenChange={supportChatContext.setOpen}
        bearerToken={bearerToken}
        enableFileUpload
        offlineFallback={offlineFallback}
        rating={rating}
      />
    );
  }

  // When context is absent, use uncontrolled mode with FAB wrapper
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <SupportChatWidget
        config={config}
        user={user}
        title="Support Chat"
        inputPlaceholder="Type your message..."
        bearerToken={bearerToken}
        enableFileUpload
        offlineFallback={offlineFallback}
        rating={rating}
      />
    </div>
  );
}
